import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getNotificationNavigationPath } from '@/utils/notificationNavigation';
import { 
  Bell, 
  ArrowRight, 
  RefreshCw, 
  BellOff, 
  Briefcase, 
  User, 
  Building2, 
  Sparkles, 
  MessageSquare, 
  Calendar, 
  AlertCircle, 
  Info, 
  FileText, 
  Megaphone, 
  Upload,
  UserCheck,
  Clock,
  Target,
  Brain,
  CheckCircle,
  Mail,
  Phone,
  Star,
  Users,
  Wrench,
  Shield,
  CreditCard,
  ScrollText
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Notification } from '@/services/notificationService';

export const NotificationsWidget: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user) {
      loadNotifications();
      return setupRealtimeSubscription();
    }
  }, [user]);

  const loadNotifications = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Get user's organization first
      const { data: userOrg } = await supabase
        .from('user_organization')
        .select('organization_uuid')
        .eq('user_uuid', user.id)
        .single();
      
      if (!userOrg?.organization_uuid) {
        console.error('User organization not found');
        return;
      }
      
      // Get recent notifications for the current user and organization
      const { data: notificationsData, error: notificationsError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .eq('organization_uuid', userOrg.organization_uuid)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!notificationsError && notificationsData) {
        setNotifications(notificationsData);
        
        // Calculate unread count from the fetched notifications
        const unreadCount = notificationsData.filter(n => !n.read_at).length;
        setUnreadCount(unreadCount);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!user) return;

    const channel = supabase
      .channel(`dashboard-notifications-${crypto.randomUUID()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotification = payload.new as Notification;
          setNotifications(prev => {
            // Ensure prev is an array
            if (!Array.isArray(prev)) {
              console.warn('Notifications state is not an array, resetting to empty array');
              return [newNotification];
            }
            return [newNotification, ...prev.slice(0, 9)];
          });
          setUnreadCount(prev => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const updatedNotification = payload.new as Notification;
          setNotifications(prev => {
            // Ensure prev is an array
            if (!Array.isArray(prev)) {
              console.warn('Notifications state is not an array, resetting to empty array');
              setUnreadCount(0);
              return [];
            }
            const updated = prev.map(n => n.id === updatedNotification.id ? updatedNotification : n);
            const newUnreadCount = updated.filter(n => !n.read_at).length;
            setUnreadCount(newUnreadCount);
            return updated;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const getNotificationIcon = (type: string, data?: any) => {
    switch (type) {
      case 'job_update':
        return <Briefcase className="h-4 w-4 text-blue-500" />;
      case 'new_application':
        return <FileText className="h-4 w-4 text-green-600" />;
      case 'application_status_change':
        return <RefreshCw className="h-4 w-4 text-orange-600" />;
      case 'candidate_update':
        return <User className="h-4 w-4 text-purple-600" />;
      case 'comment_mention':
        return <MessageSquare className="h-4 w-4 text-cyan-600" />;
      case 'new_document':
        return <Upload className="h-4 w-4 text-emerald-600" />;
      case 'system':
        return getSystemNotificationIcon(data?.category, data?.priority || 'medium');
      case 'ai_processing_complete':
        return <Bell className="h-4 w-4 text-indigo-600" />;
      case 'job':
        return <Briefcase className="h-4 w-4 text-blue-500" />;
      case 'candidate':
        return <User className="h-4 w-4 text-purple-600" />;
      case 'company':
        return <Building2 className="h-4 w-4 text-gray-600" />;
      case 'match':
        return <Sparkles className="h-4 w-4 text-indigo-600" />;
      case 'message':
        return <MessageSquare className="h-4 w-4 text-cyan-600" />;
      case 'calendar':
        return <Calendar className="h-4 w-4 text-blue-500" />;
      case 'alert':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'info':
        return <Info className="h-4 w-4 text-gray-600" />;
      default:
        return <Megaphone className="h-4 w-4 text-gray-600" />;
    }
  };

  const getSystemNotificationIcon = (category: string | null, priority: string) => {
    // Determine base icon by category
    let IconComponent;
    let baseColor;
    
    switch (category) {
      case 'maintenance':
        IconComponent = Wrench;
        baseColor = 'orange';
        break;
      case 'security':
        IconComponent = Shield;
        baseColor = 'red';
        break;
      case 'feature':
        IconComponent = Sparkles;
        baseColor = 'purple';
        break;
      case 'billing':
        IconComponent = CreditCard;
        baseColor = 'green';
        break;
      case 'policy':
        IconComponent = ScrollText;
        baseColor = 'blue';
        break;
      case 'general':
      default:
        IconComponent = Megaphone;
        baseColor = 'slate';
        break;
    }
    
    // Determine color intensity by priority
    let colorIntensity;
    switch (priority) {
      case 'critical':
        colorIntensity = '700';
        break;
      case 'high':
        colorIntensity = '600';
        break;
      case 'medium':
        colorIntensity = '500';
        break;
      case 'low':
        colorIntensity = '400';
        break;
      default:
        colorIntensity = '500';
        break;
    }
    
    const colorClass = `text-${baseColor}-${colorIntensity}`;
    
    return <IconComponent className={`h-4 w-4 ${colorClass}`} />;
  };

  const markAsRead = async (notificationIds: string[]) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .in('id', notificationIds);

      if (error) {
        throw error;
      }

      // Update local state and unread count
      setNotifications(prev => {
        // Ensure prev is an array
        if (!Array.isArray(prev)) {
          console.warn('Notifications state is not an array, resetting to empty array');
          return [];
        }
        
        const updated = prev.map(notification => 
          notificationIds.includes(notification.id) 
            ? { ...notification, read_at: new Date().toISOString() }
            : notification
        );
        
        // Calculate new unread count
        const newUnreadCount = updated.filter(n => !n.read_at).length;
        setUnreadCount(newUnreadCount);
        
        return updated;
      });
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read_at) {
      markAsRead([notification.id]);
    }

    // Use the navigation utility to determine where to go
    const navigationResult = getNotificationNavigationPath(notification);
    
    if (navigationResult.handled) {
      navigate(navigationResult.path);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return t('notifications.justNow', 'Just now');
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return t('notifications.minutesAgo', '{{minutes}}m ago', { minutes });
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return t('notifications.hoursAgo', '{{hours}}h ago', { hours });
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return t('notifications.daysAgo', '{{days}}d ago', { days });
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">
              {t('dashboard.latestNotifications', 'Latest Notifications')}
            </CardTitle>
            {unreadCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {unreadCount}
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadNotifications}
            disabled={loading}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="space-y-3">
            {Array(3).fill(0).map((_, i) => (
              <div key={i} className="flex items-start gap-3 animate-pulse">
                <div className="bg-gray-200 dark:bg-gray-700 rounded-sm p-1.5 h-6 w-6 mt-0.5"></div>
                <div className="space-y-2 flex-1">
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <BellOff className="h-12 w-12 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {t('dashboard.noNotifications', 'No notifications yet')}
            </p>
          </div>
        ) : (
          <div className="space-y-0">
            {notifications.slice(0, 5).map((notification, index) => (
              <div
                key={notification.id}
                className={cn(
                  "flex items-start gap-3 p-3 rounded-sm transition-colors cursor-pointer",
                  index % 2 === 0 ? "bg-muted/60" : "",
                  !notification.read_at && "bg-muted/30"
                )}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="text-muted-foreground mt-0.5">
                  {getNotificationIcon(notification.type, notification.data)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className={cn(
                      "text-sm leading-tight line-clamp-1",
                      !notification.read_at ? "font-bold" : "font-medium"
                    )}>
                      {notification.title}
                    </h4>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <p className="text-xs text-muted-foreground">
                        {formatTimeAgo(notification.created_at)}
                      </p>
                      {!notification.read_at && (
                        <div className="w-2 h-2 bg-primary rounded-full" />
                      )}
                    </div>
                  </div>
                  <p className={cn(
                    "text-xs mt-1 line-clamp-2",
                    !notification.read_at ? "text-foreground font-medium" : "text-muted-foreground"
                  )}>
                    {notification.message}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button
          asChild
          variant="outline"
          size="sm"
          className="w-full"
        >
          <Link to="/notifications">
            {t('dashboard.showAllNotifications', 'Show All Notifications')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}; 